<script setup lang="ts">
import { ComboboxContent, ComboboxInput, ComboboxItem, ComboboxRoot } from 'radix-vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { toast } from 'vue-sonner'
import OfkLogo from '~/assets/ofk-logo.svg'

const breakpoints = useBreakpoints(breakpointsTailwind)
const { loggedIn, config } = useUserInfo()

const router = useRouter()
const route = useRoute()
const inputEl = useTemplateRef('inputEl')
const hideHeader = ref(false)
const lastScrollTop = ref(0)
const scrollThresholdDown = ref(0) // pixels threshold before triggering header visibility change
const scrollThresholdUp = ref(60) // pixels threshold before triggering header visibility change

const queryInput = ref('')
const resetQuery = ref(false)
const selectedCompletion = ref<Suggestion>({ suggestion: '', prefix: '', truncatedPrefix: '' })

watch(() => route.query.q, (newQuery) => {
  queryInput.value = (newQuery as string) || ''
}, { immediate: true })

onMounted(() => {
  window.addEventListener('resize', (_e) => {
    // Always show header when above LG
    if (breakpoints.isGreater('lg')) {
      hideHeader.value = false
    }
  })
  window.addEventListener('scroll', (_e) => {
    // Do not hide header when above LG
    if (breakpoints.isSmaller('lg')) {
      const currentScrollTop = document.documentElement.scrollTop
      const scrollDifference = Math.abs(currentScrollTop - lastScrollTop.value)
      const isScrollingDown = currentScrollTop > lastScrollTop.value

      lastScrollTop.value = currentScrollTop

      if (currentScrollTop < 50) {
        hideHeader.value = false
        return
      }

      if (scrollDifference > (isScrollingDown ? scrollThresholdDown.value : scrollThresholdUp.value)) {
        hideHeader.value = isScrollingDown
      }
    }
  })
})

const typeaheadData = useTypeahead(queryInput, 'desktop')

watch(selectedCompletion, () => {
  if (typeof selectedCompletion.value === 'string') {
    // eslint-disable-next-line no-console
    console.warn('This should not happen: selectedCompletion is string:', selectedCompletion.value)
  } else {
    const newQuery = selectedCompletion.value.prefix + selectedCompletion.value.suggestion
    queryInput.value = newQuery
  }
  search()
})

const handleEnter = () => {
  if (queryInput.value.trim().length === 0) {
    toast.info('Bitte geben Sie einen Suchbegriff ein.')
    return
  }
  selectedCompletion.value = { suggestion: queryInput.value, prefix: '', truncatedPrefix: '' }
  search()
}

const search = () => {
  if (queryInput.value.trim().length === 0) {
    toast.info('Bitte geben Sie einen Suchbegriff ein.')
    return
  }
  if (queryInput.value === route.query.q && route.path.startsWith('/search/1')) {
    return
  }
  toast.dismiss()

  // inputEl.value?.$el?.blur()
  router.push({
    path: '/search/1',
    query: {
      ...(resetQuery.value ? {} : route.query),
      q: queryInput.value
    }
  })
  resetQuery.value = false
}

const resetSearch = () => {
  queryInput.value = ''
  resetQuery.value = true
  inputEl.value?.$el.focus()
}
const hideLoginButton = computed(() => route.path.startsWith('/login') || route.path.startsWith('/auth/callback'))
const showSearchInput = inject('showSearchInput') as () => void
</script>
<template>
  <nav class="flex sticky top-0 right-0 left-0 z-30 py-4 bg-white border-b transition-all print:hidden border-gray-10" :class="{ '-translate-y-[80px]': hideHeader }">
    <div class="w-full flex flex-row px-4 items-center" :class="[loggedIn ? 'md:px-8' : 'max-w-4xl mx-auto ']">
      <ComboboxRoot
        v-model:search-term="queryInput"
        v-model:model-value="selectedCompletion"
        as-child
        :reset-search-term-on-blur="false"
        :reset-search-term-on-select="false"
        :filter-function="(items: Record<string, any>[]) => items"
      >
        <div class="w-48 flex-shrink-0">
          <NuxtLink to="/" class="inline-block rounded-lg focus:!outline outline-1 focus:!outline-offset-8 focus:!outline-brand-mustard-400 no-focus-effect focus:fill-brand-mustard-850" tabindex="1">
            <OfkLogo class="h-12" />
          </NuxtLink>
        </div>
        <div class="flex flex-grow justify-center items-center w-full">
          <div class="flex flex-grow justify-center transition-opacity duration-700" :class="[loggedIn && config.features.includes('search') ? 'opacity-100' : 'opacity-0']">
            <div class="hidden relative w-full max-w-2xl md:block">
              <ISearch class="absolute top-0 bottom-0 left-0 my-auto ml-2.5 w-4" />
              <button v-if="queryInput.length > 0" type="button" class="absolute top-0 right-0 bottom-0 no-focus-effect focus:text-brand-mustard-500" tabindex="2" @click="resetSearch">
                <IRefresh class="my-auto mr-2.5 w-5" />
              </button>
              <ComboboxInput
                ref="inputEl"
                tabindex="1"
                class="py-1.5 pr-8 pl-8 w-full rounded-full border border-gray-50 text-md focus:!outline-1 focus:border-brand-mustard-500 focus:!outline-brand-mustard-500 focus:!outline-offset-0 focus:ring-0"
                placeholder="Suche"
                @keyup.enter.stop="handleEnter"
              />
            </div>
          </div>
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0 transform -translate-y-4"
            enter-to-class="opacity-100 transform translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100 transform translate-y-0"
            leave-to-class="opacity-0 transform -translate-y-4"
          >
            <ComboboxContent
              v-if="typeaheadData && typeaheadData.length > 0"
              class="flex !flex-row absolute right-0 left-0 top-full z-50 px-8 pb-4 space-x-2 bg-white border-b shadow-md md:flex border-gray-10"
              position="inline"
              @focus-outside="(evt: any) => {
                evt.preventDefault()
              }"
            >
              <div class="flex-grow justify-center">
                <div
                  class="px-8 mx-auto w-full max-w-2xl"
                >
                  <ComboboxItem v-if="queryInput.length" :value="queryInput">
                    {{ "" }}
                  </ComboboxItem>
                  <ComboboxItem
                    v-for="item in typeaheadData"
                    :key="item.suggestion"
                    class="flex px-8 py-4 w-full text-base rounded lg:px-4 lg:py-2 lg:-mx-4 lg:text-xl hover:!bg-brand-mustard-30/50 data-[highlighted]:bg-brand-mustard-30 cursor-pointer"
                    :value="item"
                    as="div"
                  >
                    <span class="whitespace-pre text-black/30">{{ item.truncatedPrefix }}</span>
                    <span class="truncate whitespace-pre">{{ item.suggestion }}</span>
                  </ComboboxItem>
                </div>
              </div>
            </ComboboxContent>
          </Transition>
        </div>
        <div class="flex flex-row flex-shrink-0 justify-end pl-2 lg:w-48">
          <button v-if="loggedIn && config.features.includes('search')" tabindex="-1" @click="showSearchInput">
            <ISearch class="w-6 h-6 md:hidden" />
          </button>
          <ProfileMenu v-if="loggedIn" overlay-class="ml-2" />
          <NuxtLink v-else-if="!hideLoginButton" as-child to="/login">
            <OFButton variant="secondary" class="text-lg !py-1">
              Anmelden
            </OFButton>
          </NuxtLink>
        </div>
      </ComboboxRoot>
    </div>
  </nav>
</template>
